import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Cookie from 'js-cookie';
import moment from 'moment';
import { useDate } from 'vuetify';

export default {
  isAuth() {
    const authToken = Cookie.get('_pup_access_token');
    return !!authToken;
  },

  setAppCookie(name, value, expires = 7, sameSite = 'strict', secure = true) {
    Cookie.set(name, JSON.stringify(value), {
      expires: expires,
      sameSite: sameSite,
      secure: secure,
    });
  },

  isMobileDevice() {
    switch (true) {
      case window.innerWidth <= 768:
      case navigator.userAgent.match(/Android/i):
      case navigator.userAgent.match(/webOS/i):
      case navigator.userAgent.match(/iPhone/i):
      case navigator.userAgent.match(/iPad/i):
      case navigator.userAgent.match(/iPod/i):
      case navigator.userAgent.match(/BlackBerry/i):
      case navigator.userAgent.match(/Windows Phone/i):
        return true;
      default:
        return false;
    }
  },

  formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);

    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed in JavaScript
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  },

  parseDate(date) {
    if (!date) return null;

    const [month, day, year] = date.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  },

  formatUTCDate(date) {
    return moment.parseZone(date).utc(true).format('MM-DD-YYYY h:mm:ss a');
  },

  formatDateTime(date) {
    return useDate().format(date, 'keyboardDate');
  },

  getDateTimeFromNow(date) {
    dayjs.extend(relativeTime);
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const timeZone = dayjs.tz.guess();
    return dayjs.tz(date, timeZone).fromNow();
  },

  getYearsOld(birthDate) {
    try {
      dayjs.extend(relativeTime);
      dayjs.extend(utc);
      const currentDate = dayjs(Date.now());
      const userBirthday = dayjs(birthDate);
      return currentDate.diff(userBirthday, 'year');
    } catch (e) {
      console.log(e);
      return null;
    }
  },

  formatNumberWithSuffix(number) {
    if (typeof number !== 'number' || isNaN(number)) return number;

    const formatter = Intl.NumberFormat('en', {
      notation: 'compact',
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
      roundingMode: 'floor',
    });
    return formatter.format(number);
  },

  formatPercentage(number) {
    return `${number.toFixed(2)}%`;
  },

  formatNumberWithCommas(number) {
    if (typeof number !== 'number' || isNaN(number)) return number;
    return number.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  },

  denoteAmount(amount) {
    let output = '';
    Number(amount);
    switch (true) {
      case amount >= 500 || amount <= 1000: //  $$- $500-$1000
        output = '$$';
        break;
      case amount > 1000 || amount <= 5000: //  $$$- $1000-5000
        output = '$$$';
        break;
      case amount > 5000 || amount <= 10000: //  $$$$- $5000-10000
        output = '$$$$';
        break;
      case amount > 10000: //  $$$$$- $10000
        output = '$$$$$';
        break;
      default:
        output = '$';
        break;
    }

    return output;
  },

  isValidHex(hex) {
    return /^#([0-9A-F]{3}){1,2}$/i.test(hex);
  },

  getMediaContentSettings() {
    return {
      default: {
        limit: 20,
        offset: 0,
        sort: 'createdAt',
        order_by: '',
        order_direction: 'desc',
        page: 1,
      },
    };
  },

  getHomeNavLinks() {
    return [
      { text: 'Home', to: '/' },
      { text: 'Settings', to: '/settings' },
      { text: 'Help Center', to: 'https://docs.partnrup.co/getting-started/setting-up-account' },
      { text: 'Contact Us', to: '/settings/contact-us' },
      { text: 'Privacy Policy', to: 'https://www.gen.video/privacy-policy', type: 'policy' },
      { text: 'Terms of Use', to: 'https://www.gen.video/terms-of-use', type: 'policy' },
    ];
  },

  getSettingsMenuLinks() {
    return [
      { text: 'Home', to: '/' },
      { text: 'Profile', to: '/settings/profile' },
      { text: 'Channels', to: '/settings/channels', disabled: true },
      { text: 'Password & Security', to: '/settings/password' },
      { text: 'Accounts', to: '/settings/account-details' },
      { text: 'Workspaces', to: '/settings/workspace-details' },
      { text: 'Notifications', to: '/settings/account-notifications' },
      { text: 'Help & Support', to: 'https://docs.partnrup.co/getting-started/setting-up-account', disabled: true },
      { text: 'Contact Us', to: '/settings/contact-us', disabled: true },
      { text: 'Privacy Policy', to: 'https://www.gen.video/privacy-policy' },
      { text: 'Terms of Use', to: 'https://www.gen.video/terms-of-use' },
      // { text: 'Team Members', to: '/settings/account-team' },
      // { text: 'Workspace Notifications', to: '/settings/workspace-notifications' }
    ];
  },

  getWindowPopUpSettings() {
    const left = (screen.width - 800) / 2;
    const top = (screen.height - 600) / 4;
    const strWindowFeatures = `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=600, height=801, top=${top}, left=${left}`;
    return strWindowFeatures;
  },

  getUserInitials(user) {
    const username = user.username;
    const firstName = user.first_name;
    const lastName = user.last_name;
    const combinedNames = `${user.first_name} ${user.last_name}`;
    const fullName = user?.fullname;

    if (firstName && firstName !== '') {
      return firstName[0].toUpperCase();
    }

    if (firstName && lastName) {
      const names = combinedNames.split(' ');
      const firstName = names[0].toUpperCase();
      const lastName = names[1].toUpperCase() ?? null;
      return lastName ? firstName[0] + lastName[0] : firstName[0];
    }

    if (fullName) {
      const names = fullName.split(' ');
      const firstName = names[0].toUpperCase();
      const lastName = names[1].toUpperCase() ?? null;
      return lastName ? firstName[0] + lastName[0] : firstName[0];
    }

    if (username) {
      return username[0].toUpperCase();
    }

    if (user.email) {
      return user.email[0].toUpperCase();
    }
  },

  formatPhoneNumber(phoneNumber) {
    // convert the raw number to (xxx) xxx-xxx format
    const x = phoneNumber && phoneNumber.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
    return !x[2] ? x[1] : `(${x[1]}) ${x[2]}${x[3] ? `-${x[3]}` : ''}`;
  },

  getWorkspaceRoles() {
    return [
      {
        name: 'Admin',
        role_id: 2,
        role: 'Admin',
        description: 'Someone with the same level of authority as the Owner, they just didn’t set up the Account initially. Can hide certain Elements (ie financials) from Collaborators',
      },
      {
        name: 'Editor',
        role_id: 3,
        role: 'Editor',
        description: 'Someone who can edit records but can’t configure Workspaces or Views',
      },
      {
        name: 'Client',
        role_id: 4,
        role: 'Client',
        description: 'A person invited to collaborate on a Project or at a Workspace level who can leave comments/feedback on Content and is able to approve content.',
      },
      {
        name: 'Watcher',
        role_id: 5,
        role: 'Watcher',
        description: 'Some that is provided access to Content in a Project to see it but does not have permission to comment or provide and approvals. It is limited to read-only mode.',
      },
    ];
  },

  getRoleColor(roleName) {
    switch (roleName) {
      case 'Owner':
        return 'red-darken-3';
      case 'Admin':
        return '#000000';
      case 'Editor':
        return 'indigo-darken-1';
      case 'Client':
        return '#000000';
      case 'Watcher':
        return '#000000';
      default:
        return '#000000';
    }
  },

  getSocialChannelNames() {
    return [
      { id: 'Twitter', name: 'Twitter' },
      { id: 'Twitch', name: 'Twitch' },
      { id: 'Snapchat', name: 'Snapchat' },
      { id: 'Pinterest', name: 'Pinterest' },
      { id: 'Amazon', name: 'Amazon Storefront' },
      { id: 'Blog', name: 'Personal Blog' },
      { id: 'Production', name: 'Production Video' },
    ];
  },

  getSocialMediaIcon(name) {
    switch (name) {
      case 'YouTube':
      case 'youtube':
        return '/social/youtube-icon.png';
      case 'Instagram':
      case 'instagram':
        return '/social/instagram-icon.png';
      case 'TikTok':
      case 'tiktok':
        return '/social/tiktok-icon.png';
      case 'SnapChat':
      case 'snapchat':
        return '/social/snapchat-icon.png';
      case 'Twitter':
      case 'twitter':
      case 'X':
      case 'x':
        return '/social/twitter-icon.png';
      case 'Facebook':
      case 'facebook':
        return '/social/facebook-icon.png';
      case 'Pinterest':
      case 'pinterest':
        return '/social/pinterest-icon.png';
      case 'Other':
      case 'other':
      case 'Blog':
      case 'blog':
        return '/social/wordpress-icon.png';
      case 'Twitch':
      case 'twitch':
        return '/social/twitch-icon.png';
      case 'Amazon Live':
      case 'amazon live':
        return '/social/amazon-512.webp';
      case 'Discord':
      case 'discord':
        return '/social/discord.png';
      case 'Production':
      case 'production':
        return '/social/play-button.png';
    }
  },

  getSocialChannelURLValidation(name) {
    switch (name) {
      case 'YouTube':
      case 'youtube':
        return [
          (v) => !!v || 'YouTube URL is required',
          (v) => /youtube\.com\/|channel|user|c\//i.test(v) || 'URL must start with:youtube.com/, youtube.com/c/xxxx, youtube.com/channel/xxxx, or youtube.com/user/xxxx',
        ];
      case 'Facebook':
      case 'facebook':
        return [(v) => !!v || 'Facebook URL is required', (v) => /facebook\.com\//i.test(v) || 'URL must start with: facebook.com/xxxx'];
      case 'Instagram':
      case 'instagram':
        return [(v) => !!v || 'Instagram URL is required', (v) => /instagram\.com\//i.test(v) || 'URL must start with: instagram.com/xxxx'];
      case 'TikTok':
      case 'tiktok':
        return [(v) => !!v || 'TikTok URL is required', (v) => /tiktok\.com\/@/i.test(v) || 'URL must start with: tiktok.com/@'];
      case 'Twitter':
      case 'twitter':
        return [(v) => !!v || 'Twitter URL is required', (v) => /twitter\.com\//i.test(v) || 'URL must start with: twitter.com/xxxx'];
      case 'Twitch':
      case 'twitch':
        return [(v) => !!v || 'Twitch URL is required', (v) => /twitch\.tv|.com\//i.test(v) || 'URL must start with: twitch.tv/xxxx'];
      case 'SnapChat':
      case 'snapchat':
        return [(v) => !!v || 'SnapChat URL is required', (v) => /snapchat\.com\//i.test(v) || 'URL must start with: snapchat.com/xxxx'];
      case 'Pinterest':
      case 'pinterest':
        return [(v) => !!v || 'Pinterest URL is required', (v) => /pinterest\.com\//i.test(v) || 'URL must start with: pinterest.com/xxxx'];
      case 'Amazon':
      case 'amazon':
        return [(v) => !!v || 'Amazon URL is required', (v) => /amazon\.com\/\/|shop\/|live\/channel\//i.test(v) || 'URL must start with: amazon.com/shop/, amazon.com/live/channel/'];
    }
  },

  getChannelPlaceHolderText(name) {
    switch (name) {
      case 'YouTube':
      case 'youtube':
        return 'www.youtube.com/';
      case 'Facebook':
      case 'facebook':
        return 'www.facebook.com/user';
      case 'Instagram':
      case 'instagram':
        return 'www.instagram.com/user';
      case 'TikTok':
      case 'tiktok':
        return 'www.tiktok.com/@';
      case 'Twitter':
      case 'twitter':
        return 'www.twitter.com/user';
      case 'Twitch':
      case 'twitch':
        return 'www.twitch.tv/user';
      case 'SnapChat':
      case 'snapchat':
        return 'www.snapchat.com/user';
      case 'Pinterest':
      case 'pinterest':
        return 'www.pinterest.com/user';
      case 'Amazon':
      case 'amazon':
        return 'www.amazon.com/storefront';
      case 'Production':
      case 'production':
        return 'www.production-url-link.com';
      case 'Blog':
      case 'blog':
        return 'www.blog.com';
    }
  },

  getImageFileTypes() {
    return ['image/jpg', 'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp', 'image/tiff', 'image/x-icon', 'image/vnd.microsoft.icon', 'image/vnd.wap.wbmp'];
  },

  getVideoFileTypes() {
    return [
      'video/h264',
      'video/h261',
      'video/jpeg',
      'video/mp4',
      'video/webm',
      'video/ogg',
      'video/3gpp',
      'video/3gpp2',
      'video/quicktime',
      'video/x-ms-wmv',
      'video/x-flv',
      'video/x-msvideo',
      'video/x-ms-asf',
      'video/x-matroska',
      'video/flv',
      'video/ogv',
      'video/avi',
      'video/wmv',
    ];
  },

  getExcelFileTypes() {
    return ['.csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'application/vnd.oasis.opendocument.spreadsheet', '.xls', '.xlsx'];
  },
  convertUrlsToLinks(text, length = 50) {
    if (typeof text !== 'string') {
      return text; // Return the input as is
    }
    // Regular expression to match URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    // Replace URLs with anchor tags
    return text.replace(urlRegex, (url) => {
      // Remove trailing punctuation if present
      const cleanUrl = url.replace(/[.,;:!?]$/, '');
      const urlObject = new URL(url);
      const hostname = urlObject.host;
      // Create the anchor tag
      return `<a href="${cleanUrl}" target="_blank">${hostname}</a>`;
    });
  },
};
